import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Image,
  Badge,
  useColorModeValue,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getRewards, redeemReward, RewardItem } from '../../services/engagement';

export type RewardsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  availablePoints?: number;
  onRedeemed?: () => void;
};

const RewardsModal: React.FC<RewardsModalProps> = ({ isOpen, onClose, availablePoints = 0, onRedeemed }) => {
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['engagement', 'rewards'],
    queryFn: getRewards,
    enabled: isOpen,
    staleTime: 10000,
  });

  const redeemMut = useMutation({
    mutationFn: (id: number) => redeemReward(id),
    onSuccess: async (res) => {
      toast({ title: res.status === 'approved' ? 'Odměna aktivována' : 'Žádost o odměnu odeslána', status: 'success', duration: 3000 });
      await qc.invalidateQueries({ queryKey: ['engagement', 'profile'] });
      if (onRedeemed) onRedeemed();
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.error || 'Nepodařilo se uplatnit odměnu';
      toast({ title: 'Chyba', description: msg, status: 'error', duration: 3500 });
    }
  });

  const items = (q.data || []) as RewardItem[];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Odměny</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {q.isLoading ? (
            <HStack><Spinner size="sm" /><Text>Načítám…</Text></HStack>
          ) : (
            <VStack align="stretch" spacing={3}>
              <HStack spacing={3} bg={cardBg} p={3} borderRadius="md">
                <Badge colorScheme="blue">Dostupné body: {availablePoints}</Badge>
              </HStack>
              {items.map((it) => (
                <HStack key={it.id} spacing={3} p={2} borderWidth="1px" borderRadius="md" align="center">
                  {it.image_url && (
                    <Image src={it.image_url} alt={it.name} boxSize="48px" objectFit="cover" borderRadius="md" />
                  )}
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontWeight="600">{it.name}</Text>
                    <></>
                  </VStack>
                  <Badge>{it.cost_points} bodů</Badge>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => redeemMut.mutate(it.id)}
                    isLoading={redeemMut.isPending}
                    isDisabled={availablePoints < (it.cost_points || 0)}
                  >
                    Uplatnit
                  </Button>
                </HStack>
              ))}
              {items.length === 0 && (
                <Text color="gray.500">Zatím nejsou k dispozici žádné odměny.</Text>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Zavřít</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RewardsModal;
